import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NavController } from '@ionic/angular';
import { Logger, LoggingService } from 'ionic-logging-service';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { EnvironmentService } from '../environment/environment.service';
import { EventService } from '../events/event.service';
import { StorageService } from '../storage/storage.service';
import { TenantService, TENANT_LIST_LENGTH_KEY } from '../tenant/tenant.service';
import { UserService } from '../user/user.service';
import { Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class LogoutService {

  private logger: Logger;
  private storedItemKeys = ['lastUsedTenant', 'idpLoginToken', TENANT_LIST_LENGTH_KEY];

  constructor(
    private authService: AuthService,
    private environmentService: EnvironmentService,
    private eventService: EventService,
    private httpClient: HttpClient,
    private loggingService: LoggingService,
    public navController: NavController,
    private storageService: StorageService,
    private tenantService: TenantService,
    private userService: UserService
  ) {
    this.logger = this.loggingService.getLogger("[LogoutService]");
    const methodName = "ctor";
    this.logger.entry(methodName);
  }

  public signOutUser(): Observable<void> {
    this.logger.info('signOutUser()');
    // Revoke the tokens server-side and clear token storage first. If revocation
    // fails (offline, server error, already-expired token) we still continue
    // clearing local user/tenant/Pusher state so the app ends up signed out.
    return this.authService.deAuthenticateUser().pipe(
      take(1),
      catchError(err => {
        this.logger.error('signOutUser() deAuthenticateUser error: ' + JSON.stringify(err));
        return of(void 0);
      }),
      switchMap(() => this.storageService.removeStoredItems(this.storedItemKeys)),
      switchMap(() => this.userService.resetUser()),
      switchMap(() => this.tenantService.resetTenant()),
      switchMap(() => this.eventService.resetPusher()),
      catchError(err => {
        this.logger.error('signOutUser() chain error: ' + JSON.stringify(err));
        return of(err); // or throwError(err) if you want to propagate
      }),
      map(() => void 0),
    );
  }

  // Browser-only final sign-out step: POST ENDPOINT_SIGN_OUT_BROWSER so the auth
  // server clears the SSO session cookie. Called from NetworkCheckPage (not from
  // signOutUser) so the 401/403 auto sign-out path does not end the SSO session.
  // withCredentials sends the session cookie; responseType 'text' because /logout
  // responds with text rather than JSON. The server returns 200 OK (no redirect) —
  // the app itself routes on to sign-in afterwards. Failures are tolerated — the
  // caller still proceeds to the sign-in app.
  public endBrowserSsoSession(): Observable<void> {
    this.logger.info('endBrowserSsoSession()');
    const signOutUrl: string = this.environmentService.getEnvironmentEndpoint['ENDPOINT_SIGN_OUT_BROWSER'];
    return this.httpClient.post(signOutUrl, null, { withCredentials: true, responseType: 'text' }).pipe(
      take(1),
      catchError(err => {
        this.logger.error('endBrowserSsoSession() error: ' + JSON.stringify(err));
        return of(null);
      }),
      map(() => void 0),
    );
  }
}